import React, { Component } from "react";
import axios from "axios";
import moment from "moment";
import * as constants from "../../Constatnts";
import * as actionTypes from "../../actionTypes";
import Window from "../2/Controls/Window";
import Input from "../2/Controls/Input";
import Loader from "../Loader";
import BatchSelection from "../Inventory/BatchSelection";
import ProductVariants from "./ProductVariants";
import Print from "./Print";
import { printFormatting } from "./SalesFunctions";
import "./WhatsAppOrders.css";
import alertSound from "../../audio/audio/electronic-school-bell-81033.mp3";

// Online orders (Sales > Online Orders): WhatsApp orders and the orders of the online store (omni web)
//   Pending -> Accept -> Packed / Ready -> Out for delivery -> Delivered
// WhatsApp orders: when an order is packed it is converted into a Sales Invoice (the invoice reduces the stock):
// batch products need their batch(es) and variant products their variant, chosen in a popup like in a sales invoice.
// Online store orders are already sales, they are managed like in the Delivery List
// (accept, ready for pickup, out for delivery, delivered - the balance is collected in the sales payment screen).
// The new orders come in by themselves: the list is reloaded every 15 seconds.

const REFRESH_SECONDS = 15;

// tab -> DeliveryStatus saved on the order
const TABS = [
  { key: "pending", label: "Pending", status: "Pending" },
  { key: "accepted", label: "Accepted", status: "Confirmed" },
  { key: "packed", label: "Packed", status: "Ready For Pickup" },
  { key: "out", label: "Out for delivery", status: "Out For Delivery" },
  { key: "delivered", label: "Delivered", status: "Delivered" },
  { key: "cancelled", label: "Cancelled", status: "Cancelled" },
];

const money = (amount) => "₹" + Number(amount || 0).toFixed(2);

class WhatsAppOrders extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: "pending",
      orders: [],
      counts: {},
      loading: false,
      busyKey: "", // order being accepted / delivered / cancelled
      packOrder: null, // order being packed (popup)
      selections: {}, // line index -> { variant, picks: [{ batch, qty }] }
      batchLine: null, // { index, product } -> batch popup
      variantLine: null, // { index, product } -> variant popup
      packing: false,
      outOrder: null, // order going out for delivery (popup)
      deliveryPersonName: "",
      deliveryPersonKey: "", // employee key, "new" for another person, "" nothing selected
      deliveryPersonMobileNo: "",
      employeeList: [],
      searchText: "", // filters the orders of the open tab
      print: false,
      printData: null,
      deliverOrder: null, // cash on delivery order being delivered: payment mode popup
      deliveryPaymentModeID: "",
    };
    this.lastPendingCount = null;
    this.timer = null;
    this.loadOrders = this.loadOrders.bind(this);
  }

  componentDidMount() {
    this.loadEmployees();
    this.loadOrders(false);
    this.timer = setInterval(() => this.loadOrders(true), REFRESH_SECONDS * 1000);
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  // active employees of the store: the delivery person of an order going out
  async loadEmployees() {
    try {
      const employees = await this.props.getEmployees(
        this.props.user.user.RegistrationID,
        this.props.user.store.key,
        0,
        1000,
        "active",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        ""
      );
      this.setState({ employeeList: (employees && employees.searchResult) || [] });
    } catch (error) {
      console.error("Employees not loaded:", error);
    }
  }

  onDeliveryPersonSelected(value) {
    let name = "";
    let mobileNo = "";
    const employee = this.state.employeeList.find((e) => e.key === value);
    if (employee) {
      name = (employee.EmployeeFirstName + " " + (employee.EmployeeLastName || "")).trim();
      mobileNo = employee.PhoneNo || "";
    }
    this.setState({ deliveryPersonKey: value, deliveryPersonName: name, deliveryPersonMobileNo: mobileNo });
  }

  closeOutWindow() {
    this.setState({ outOrder: null, deliveryPersonName: "", deliveryPersonKey: "", deliveryPersonMobileNo: "" });
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------
  headers() {
    return { headers: { Authorization: "Bearer " + this.props.user.uat } };
  }

  searchBody(status, size) {
    const query = {
      bool: {
        must: [
          { term: { RegistrationID: this.props.user.user.RegistrationID } },
          // old: only WhatsApp orders
          // { match_phrase: { RequestSource: "whatsapp" } },
          // { exists: { field: "ExternalTransactionNo" } },
          // only the sources managed here (not pincode, ONDC ... orders)
          // WhatsApp: orders only, the invoices made from them have no ExternalTransactionNo
          {
            bool: {
              should: [
                {
                  bool: {
                    must: [{ match_phrase: { RequestSource: "whatsapp" } }, { exists: { field: "ExternalTransactionNo" } }],
                  },
                },
                { match_phrase: { RequestSource: "omni web" } },
              ],
              minimum_should_match: 1,
            },
          },
        ],
        must_not: [],
      },
    };
    if (status) query.bool.must.push({ term: { "DeliveryStatus.keyword": status } });
    if (status !== "Cancelled") query.bool.must_not.push({ exists: { field: "CancellationReason" } });
    return {
      from: 0,
      size: size,
      track_total_hits: true,
      query: query,
      sort: [{ TransactionDate: { order: status === "Pending" || status === "Confirmed" ? "asc" : "desc" } }],
    };
  }

  // a new order came in while this page is open. The push notification (Main.js) plays its own sound when it arrives;
  // wait a little for it and play this one only when it has not, so there is one sound, not two
  playNewOrderSound() {
    setTimeout(() => {
      if (Date.now() - (window.lastOrderAlertAt || 0) < 30000) return;
      const audio = new Audio(alertSound);
      audio.play().catch((error) => console.error("Order sound not played:", error));
    }, 8000);
  }

  isWhatsApp(order) {
    return !!order.RequestSource && String(order.RequestSource).toLowerCase() === "whatsapp";
  }

  // where the order came from
  channelLabel(order) {
    const source = String(order.RequestSource || "").toLowerCase();
    if (source === "whatsapp") return "WhatsApp";
    if (source === "omni web" || source === "") return "Online Store";
    return order.RequestSource;
  }

  customerPhone(order) {
    return (order.DeliveryCustomer && order.DeliveryCustomer.PhoneNo) || (order.Customer && order.Customer.PhoneNo) || "";
  }

  // the orders of the open tab, and how many orders each tab has
  async loadOrders(silent) {
    const tab = TABS.find((t) => t.key === this.state.tab);
    if (!silent) this.setState({ loading: true });
    try {
      const [list, counts] = await Promise.all([
        axios.post(constants.API_URL + "/transaction/getsales", { text: this.searchBody(tab.status, 50) }, this.headers()),
        Promise.all(
          TABS.map((t) =>
            axios
              .post(constants.API_URL + "/transaction/getsales", { text: { ...this.searchBody(t.status, 0), sort: [] } }, this.headers())
              .then((res) => res.data.text.hits.total.value)
              .catch(() => 0)
          )
        ),
      ]);

      const orders = list.data.text.hits.hits.map((hit) => ({ key: hit._id, ...hit._source }));
      const countByTab = {};
      TABS.forEach((t, i) => (countByTab[t.key] = counts[i]));

      // a new pending order came in while the page is open
      if (this.lastPendingCount !== null && countByTab.pending > this.lastPendingCount) {
        // this.props.getToastr("New WhatsApp order received");
        this.props.getToastr("New Online order received");
        this.playNewOrderSound();
      }
      this.lastPendingCount = countByTab.pending;

      this.setState({ orders: orders, counts: countByTab, loading: false });
    } catch (error) {
      console.error("WhatsApp orders not loaded:", error);
      this.setState({ loading: false });
    }
  }

  async post(path, body) {
    const res = await axios.post(constants.API_URL + path, body, this.headers());
    if (!res.data || String(res.data.code) !== "0") throw new Error((res.data && res.data.message) || "Failed");
    return res.data;
  }

  // ---------------------------------------------------------------------------
  // What a line needs when the order is packed
  // ---------------------------------------------------------------------------
  configProduct(product) {
    const products = (this.props.config && this.props.config.Products) || [];
    return products.find((p) => p.key === product.key) || product;
  }

  // the picture of an order line: saved on the line's product, else the one in the product list
  productImage(product) {
    const own = product && product.Images && product.Images.length > 0 ? product.Images[0].ImagePath : "";
    if (own) return own;
    const listed = product ? this.configProduct(product) : null;
    return listed && listed.Images && listed.Images.length > 0 ? listed.Images[0].ImagePath || "" : "";
  }

  renderProductImage(product) {
    const src = this.productImage(product);
    return src ? (
      <img className="wao-img" src={src} alt={(product && product.Name) || ""} loading="lazy" />
    ) : (
      <span className="wao-img wao-img-empty" />
    );
  }

  hasVariants(product) {
    const products = (this.props.config && this.props.config.Products) || [];
    return products.some((p) => p.ParentProductID === product.key && !p.IsDeleted);
  }

  isBatchProduct(product) {
    return !!(product && product.InventoryType && String(product.InventoryType).toLowerCase() === "batch tracking" && product.ProductTypeID < 3);
  }

  // delivery / packaging charge lines are services: nothing to choose
  isStockLine(line) {
    return line.Product && line.Product.ProductTypeID < 3;
  }

  // "variant" / "batch" / "" for an order line
  lineNeeds(line) {
    if (!this.isStockLine(line)) return "";
    if (this.hasVariants(line.Product)) return "variant";
    return this.isBatchProduct(this.configProduct(line.Product)) ? "batch" : "";
  }

  // the product that is packed for a line: the chosen variant, else the line's product
  packedProduct(line, selection) {
    if (selection && selection.variant) return selection.variant;
    return this.configProduct(line.Product);
  }

  // how many of an order line the shop has (all of it unless changed in the pack popup). NaN: not a valid number
  haveQty(line, selection) {
    if (!selection || selection.qty === undefined) return Number(line.Qty);
    const qty = selection.qty === "" ? NaN : Number(selection.qty);
    return qty >= 0 && qty <= Number(line.Qty) ? qty : NaN;
  }

  // the order with the quantities the shop has: new total, what the customer pays on delivery / is refunded
  packTotals(order, selections) {
    let total = 0;
    let anyPacked = false;
    order.LineItems.forEach((line, i) => {
      if (!this.isStockLine(line)) {
        total += Number(line.Amount); // delivery / packaging charge
        return;
      }
      const have = this.haveQty(line, selections[i]);
      if (!(have >= 0)) return;
      if (have > 0) anyPacked = true;
      total += (Number(line.Amount) * have) / Number(line.Qty);
    });
    total = Math.round(total * 100) / 100;
    const paid = Number(order.BillPaidAmount) > 0 ? Number(order.BillPaidAmount) : 0;
    return {
      total: total,
      anyPacked: anyPacked,
      paid: paid,
      refund: paid > 0 ? Math.max(0, Math.round((paid - total) * 100) / 100) : 0,
      payOnDelivery: paid > 0 ? Math.max(0, Math.round((total - paid) * 100) / 100) : total,
    };
  }

  // what the customer still pays at the door: after packing it is the invoice balance (the packed items can cost less)
  codDue(order) {
    return order.PackedBalanceAmount !== undefined ? Number(order.PackedBalanceAmount) : Number(order.BalanceAmount);
  }

  // is every line ready: variant chosen, and (batch product) the batch quantities add up to the ordered quantity
  packReady(order, selections) {
    for (let i = 0; i < order.LineItems.length; i++) {
      const line = order.LineItems[i];
      if (!this.isStockLine(line)) continue;
      const selection = selections[i] || {};
      const have = this.haveQty(line, selection);
      if (!(have >= 0)) return false; // not a valid quantity
      if (have === 0) continue; // none available: nothing to choose
      if (this.hasVariants(line.Product) && !selection.variant) return false;
      if (this.isBatchProduct(this.packedProduct(line, selection))) {
        const total = (selection.picks || []).reduce((sum, p) => sum + Number(p.qty || 0), 0);
        if (Math.round(total * 1000) !== Math.round(have * 1000)) return false;
      }
    }
    return this.packTotals(order, selections).anyPacked;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  async onAccept(order) {
    this.setState({ busyKey: order.key });
    // WhatsApp: SMS number empty, the customer is already chatting with the shop on WhatsApp
    const ok = await this.props.orderConfirmed(order, this.isWhatsApp(order) ? "" : this.customerPhone(order));
    this.setState({ busyKey: "" });
    if (ok) {
      this.props.getToastr("Order " + order.TransactionNo + " accepted");
      this.loadOrders(true);
    } else {
      alert("The order could not be accepted. Please try again.");
    }
  }

  async onCancel(order) {
    // a packed WhatsApp order has a Sales Invoice: it is cancelled with the order (the stock comes back)
    const invoiceNote = order.InvoiceNo ? "\n\nSales Invoice " + order.InvoiceNo + " will be cancelled too." : "";
    const reason = window.prompt("Reason for cancelling order " + order.TransactionNo + "?" + invoiceNote);
    if (reason === null) return;
    if (reason.trim() === "") {
      alert("Please enter a reason");
      return;
    }
    this.setState({ busyKey: order.key });
    try {
      await this.post("/transaction/sales/" + order.key + "/cancel", { CancellationReason: reason.trim() });
      this.props.getToastr("Order " + order.TransactionNo + " cancelled");
      this.loadOrders(true);
    } catch (error) {
      alert("The order could not be cancelled: " + error.message);
    }
    this.setState({ busyKey: "" });
  }

  onPack(order) {
    // old: plain products were packed after a yes / no question
    // const needsSelection = order.LineItems.some((l) => this.lineNeeds(l) !== "");
    // if (!needsSelection) { if (window.confirm(...)) this.convertToInvoice(order, {}); return; }
    // now the shop is always asked how many of each item it has
    this.setState({ packOrder: order, selections: {} });
  }

  // the shop has this many of an order line (the batches picked for the old quantity are dropped)
  onHaveQtyChange(index, value) {
    const current = this.state.selections[index] || {};
    this.setState({ selections: { ...this.state.selections, [index]: { ...current, qty: value, picks: [] } } });
  }

  async convertToInvoice(order, selections) {
    const payload = {};
    Object.keys(selections).forEach((index) => {
      const s = selections[index];
      payload[index] = {};
      if (s.qty !== undefined) payload[index].qty = Number(s.qty);
      if (s.variant) payload[index].variantProductID = s.variant.key;
      if (s.picks) payload[index].batches = s.picks.map((p) => ({ batchID: p.batch.key, qty: Number(p.qty) }));
    });

    this.setState({ packing: true });
    try {
      const result = await this.post("/transaction/sales/" + order.key + "/convert-to-invoice", { selections: payload });
      this.props.getToastr("Packed. Sales Invoice " + result.invoiceNo + " created");
      const refund = result.refund || {};
      if (refund.due > 0) {
        if (refund.status === "initiated") this.props.getToastr("Refund of " + money(refund.due) + " sent to the customer's payment method");
        else alert("The refund of " + money(refund.due) + " could not be made automatically" + (refund.message ? " (" + refund.message + ")" : "") + ". Please refund it from Razorpay.");
        // the items not packed are returned on an "Online Order Return" so the reports match
        const onlineReturn = result.onlineReturn || {};
        if (onlineReturn.created) this.props.getToastr("Online Order Return " + onlineReturn.no + " created");
        else alert("The Online Order Return was not created: " + (onlineReturn.message || "unknown error") + "\nThe reports will not show the reduced amount until it is made.");
      }
      this.setState({ packing: false, packOrder: null, selections: {}, tab: "packed" }, () => this.loadOrders(false));
    } catch (error) {
      this.setState({ packing: false });
      const message = (error.response && error.response.data && error.response.data.message) || error.message;
      alert("Could not pack the order: " + message);
    }
  }

  // payment modes that can be taken at the door (wallet, loyalty and return redemption note need their own steps)
  codPaymentModes() {
    const excluded = ["wallet", "loyalty", "return redemption note"];
    return ((this.props.config && this.props.config.PaymentTypes) || []).filter(
      (p) => p.ShowInSales && !p.IsDeleted && !excluded.includes(String(p.PaymentType).toLowerCase())
    );
  }

  async onDelivered(order) {
    // WhatsApp cash on delivery: ask how the customer paid, the payment is recorded against the invoice
    if (this.isWhatsApp(order) && this.codDue(order) > 0) {
      const modes = this.codPaymentModes();
      const cash = modes.find((p) => String(p.PaymentType).toLowerCase() === "cash");
      this.setState({ deliverOrder: order, deliveryPaymentModeID: cash ? cash.key : modes.length > 0 ? modes[0].key : "" });
      return;
    }
    if (!window.confirm("Mark order " + order.TransactionNo + " as delivered?")) return;
    await this.markDelivered(order, "");
  }

  async markDelivered(order, paymentModeID) {
    this.setState({ busyKey: order.key });
    try {
      await axios.put(constants.API_URL + "/markdelivered/" + order.key, { PaymentModeID: paymentModeID }, this.headers());
      this.props.getToastr("Order " + order.TransactionNo + " delivered");
      this.loadOrders(true);
      // online store order with a balance: collect it in the sales payment screen, like in the Delivery List
      if (!this.isWhatsApp(order) && Number(order.BalanceAmount) > 0) { // online store: the sale itself
        this.setState({ busyKey: "" });
        this.openSale(order, true);
        return;
      }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || "";
      alert("The order could not be updated. " + (message || "Please try again."));
    }
    this.setState({ busyKey: "" });
  }

  // packed order: print the invoice of a WhatsApp order, the order itself for an online store order
  async onPrint(order) {
    const key = this.isWhatsApp(order) && order.InvoiceKey ? order.InvoiceKey : order.key;
    const sale = await this.props.getSaleDetails(key);
    if (!sale) {
      alert("The order could not be loaded for printing. Please try again.");
      return;
    }
    let printConfigs = this.props.config.PrintConfigs.filter((e) => !e.IsDeleted && e.TransactionTypeID === sale.TransactionType.key);
    if (printConfigs.length === 0) {
      // the order's transaction type ("Online Orders") has no layout yet: the Sales Invoice layout is used
      const invoiceType = (this.props.config.TransactionTypes || []).find(
        (t) => !t.IsDeleted && String(t.Module).toLowerCase() === "sales" && String(t.TransactionType).trim().toLowerCase() === "sales invoice"
      );
      printConfigs = invoiceType ? this.props.config.PrintConfigs.filter((e) => !e.IsDeleted && e.TransactionTypeID === invoiceType.key) : [];
    }
    if (printConfigs.length === 0) {
      alert("Print setup does not exists");
      return;
    }
    // printFormatting reads the date as a date / date string
    const date = sale.TransactionDate;
    if (date && (date.seconds || date._seconds)) sale.TransactionDate = new Date((date.seconds || date._seconds) * 1000);
    this.setState({
      print: true,
      printData: { Sale: printFormatting(sale), PrintConfig: printConfigs[0] },
    });
  }

  // online store order: ready for pickup (the Packed tab)
  async onReady(order) {
    this.setState({ busyKey: order.key });
    const ok = await this.props.readyForPickup(order, this.customerPhone(order));
    this.setState({ busyKey: "" });
    if (ok) {
      this.props.getToastr("Order " + order.TransactionNo + " is ready");
      this.loadOrders(true);
    } else {
      alert("The order could not be updated. Please try again.");
    }
  }

  // online store order: the sale itself is opened in the sales screen (edit, or payment of the balance)
  async openSale(order, showPayment) {
    const saleEdit = await this.props.getSaleDetails(order.key);
    saleEdit.key = order.key;
    const state = {
      type: actionTypes.SALE_EDIT,
      key: saleEdit.key,
      transactionType: saleEdit.TransactionType,
      sale: saleEdit,
      module: "deliverylist",
    };
    if (showPayment) state.show = "payment";
    this.props.history.push({ pathname: "/home/sales/sales", state: state });
  }

  async onOutForDelivery() {
    const order = this.state.outOrder;
    this.setState({ busyKey: order.key });
    try {
      await this.post("/outfordelivery", {
        sale: order,
        deliveryPersonName: this.state.deliveryPersonName.trim(),
        // "new" is another person, not an employee
        deliveryPersonKey: this.state.deliveryPersonKey === "new" ? "" : this.state.deliveryPersonKey,
        deliveryPersonMobileNo: this.state.deliveryPersonMobileNo.trim(),
      });
      this.props.getToastr("Order " + order.TransactionNo + " is out for delivery");
      this.closeOutWindow();
      this.loadOrders(true);
    } catch (error) {
      alert("The order could not be updated: " + error.message);
    }
    this.setState({ busyKey: "" });
  }

  // ---------------------------------------------------------------------------
  // Batch / variant choice
  // ---------------------------------------------------------------------------
  onVariantChosen(variantProduct) {
    const { variantLine, selections } = this.state;
    // a new variant: the batch choice of this line starts again
    const next = { ...selections, [variantLine.index]: { ...(selections[variantLine.index] || {}), variant: variantProduct, picks: [] } };
    this.setState({ selections: next, variantLine: null });
  }

  // selected batches -> quantity taken from each, oldest listed first, until the ordered quantity is covered
  onBatchesChosen(batches) {
    const { batchLine, selections, packOrder } = this.state;
    const line = packOrder.LineItems[batchLine.index];
    const needed = this.haveQty(line, selections[batchLine.index]);
    let remaining = needed;
    const picks = [];
    batches.forEach((batch) => {
      if (remaining <= 0) return;
      const take = Math.min(remaining, Number(batch.Qty));
      if (take > 0) {
        picks.push({ batch: batch, qty: take });
        remaining = Math.round((remaining - take) * 1000) / 1000;
      }
    });
    if (remaining > 0) {
      alert("The selected batches have " + (needed - remaining) + " in stock, " + needed + " are needed. Select more batches.");
    }
    const current = selections[batchLine.index] || {};
    this.setState({ selections: { ...selections, [batchLine.index]: { ...current, picks: picks } }, batchLine: null });
  }

  onPickQtyChange(lineIndex, pickIndex, value) {
    const selections = { ...this.state.selections };
    const picks = selections[lineIndex].picks.map((p, i) => (i === pickIndex ? { ...p, qty: value } : p));
    selections[lineIndex] = { ...selections[lineIndex], picks: picks };
    this.setState({ selections: selections });
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  renderAddress(order) {
    const a = order.DeliveryCustomerAddress;
    if (!a) return "";
    return [a.Address1, a.Address2, a.City, a.State, a.PostalCode].filter((part) => part && String(part).trim()).join(", ");
  }

  // online store orders: same steps as the Delivery List, there is no invoice to make
  renderStoreActions(order) {
    const busy = this.state.busyKey === order.key;
    const status = order.DeliveryStatus;
    const outButton = (
      <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.setState({ outOrder: order })}>
        Out for delivery
      </button>
    );
    const deliveredButton = (
      <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.onDelivered(order)}>
        Delivered
      </button>
    );
    const cancelButton = (
      <button className="wao-btn wao-btn-light" disabled={busy} onClick={() => this.onCancel(order)}>
        Cancel
      </button>
    );
    const editButton = (
      <button className="wao-btn wao-btn-light" disabled={busy} onClick={() => this.openSale(order, false)}>
        Edit
      </button>
    );
    const printButton = (
      <button className="wao-btn wao-btn-light" onClick={() => this.onPrint(order)}>
        Print
      </button>
    );
    if (status === "Pending") {
      return (
        <div className="wao-actions">
          <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.onAccept(order)}>
            Accept
          </button>
          {editButton}
          {cancelButton}
        </div>
      );
    }
    if (status === "Confirmed") {
      return (
        <div className="wao-actions">
          <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.onReady(order)}>
            Ready
          </button>
          {outButton}
          {editButton}
          {cancelButton}
        </div>
      );
    }
    if (status === "Ready For Pickup") {
      return (
        <div className="wao-actions">
          {outButton}
          {deliveredButton}
          {printButton}
          {cancelButton}
        </div>
      );
    }
    if (status === "Out For Delivery") {
      return (
        <div className="wao-actions">
          {deliveredButton}
          {cancelButton}
        </div>
      );
    }
    if (status === "Cancelled" && order.CancellationReason) {
      return <div className="wao-muted">Reason: {order.CancellationReason}</div>;
    }
    return null;
  }

  renderActions(order) {
    if (!this.isWhatsApp(order)) return this.renderStoreActions(order);
    const busy = this.state.busyKey === order.key;
    const status = order.DeliveryStatus;

    if (status === "Pending") {
      return (
        <div className="wao-actions">
          <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.onAccept(order)}>
            Accept
          </button>
          <button className="wao-btn wao-btn-light" disabled={busy} onClick={() => this.onCancel(order)}>
            Cancel
          </button>
        </div>
      );
    }
    if (status === "Confirmed") {
      return (
        <div className="wao-actions">
          <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.onPack(order)}>
            Packed
          </button>
          <button className="wao-btn wao-btn-light" disabled={busy} onClick={() => this.onCancel(order)}>
            Cancel
          </button>
        </div>
      );
    }
    if (status === "Ready For Pickup") {
      return (
        <div className="wao-actions">
          <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.setState({ outOrder: order })}>
            Out for delivery
          </button>
          <button className="wao-btn wao-btn-light" onClick={() => this.onPrint(order)}>
            Print
          </button>
          <button className="wao-btn wao-btn-light" disabled={busy} onClick={() => this.onCancel(order)}>
            Cancel
          </button>
          {order.InvoiceKey && (
            <button className="wao-btn wao-btn-light" onClick={() => this.props.history.push("/home/sales/viewsales/" + order.InvoiceKey)}>
              Invoice {order.InvoiceNo}
            </button>
          )}
        </div>
      );
    }
    if (status === "Out For Delivery") {
      return (
        <div className="wao-actions">
          <button className="wao-btn wao-btn-primary" disabled={busy} onClick={() => this.onDelivered(order)}>
            Delivered
          </button>
          {order.InvoiceKey && (
            <button className="wao-btn wao-btn-light" onClick={() => this.props.history.push("/home/sales/viewsales/" + order.InvoiceKey)}>
              Invoice {order.InvoiceNo}
            </button>
          )}
        </div>
      );
    }
    if (status === "Delivered" && order.InvoiceKey) {
      return (
        <div className="wao-actions">
          <button className="wao-btn wao-btn-light" onClick={() => this.props.history.push("/home/sales/viewsales/" + order.InvoiceKey)}>
            Invoice {order.InvoiceNo}
          </button>
        </div>
      );
    }
    if (status === "Cancelled" && order.CancellationReason) {
      return <div className="wao-muted">Reason: {order.CancellationReason}</div>;
    }
    return null;
  }

  // order no, customer name / phone / email, delivery address and item names
  orderMatches(order, text) {
    const customer = order.Customer || {};
    const deliveryCustomer = order.DeliveryCustomer || {};
    const haystack = [
      order.TransactionNo,
      order.InvoiceNo,
      order.ExternalTransactionNo,
      this.channelLabel(order),
      customer.FirstName,
      customer.LastName,
      customer.PhoneNo,
      customer.EmailID,
      deliveryCustomer.FirstName,
      deliveryCustomer.LastName,
      deliveryCustomer.PhoneNo,
      this.renderAddress(order),
      ...(order.LineItems || []).map((l) => l.Product && l.Product.Name),
    ]
      .filter((v) => v)
      .join(" ")
      .toLowerCase();
    // every word typed has to be found, in any order
    return text
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w)
      .every((w) => haystack.includes(w));
  }

  renderOrder(order) {
    const paid = Number(order.BillPaidAmount) > 0;
    const customer = order.Customer || {};
    const name = ((customer.FirstName || "") + " " + (customer.LastName || "")).trim();
    return (
      <div className="wao-card" key={order.key}>
        <div className="wao-card-head">
          <div>
            <span className="wao-order-no">{order.TransactionNo}</span>
            <span className={"wao-channel " + (this.isWhatsApp(order) ? "wao-channel-whatsapp" : "wao-channel-store")}>{this.channelLabel(order)}</span>
            <span className="wao-muted"> &middot; {moment(order.TransactionDate).fromNow()}</span>
          </div>
          <div className={"wao-pay " + (paid ? "wao-pay-paid" : "wao-pay-cod")}>
            {paid ? "Paid online" : "Cash on delivery " + money(this.codDue(order))}
          </div>
        </div>
        <div className="wao-card-body">
          <div className="wao-col">
            <div className="wao-label">Customer</div>
            <div>{name || "Customer"}</div>
            <div className="wao-muted">{(order.DeliveryCustomer && order.DeliveryCustomer.PhoneNo) || customer.PhoneNo}</div>
            <div className="wao-muted">{this.renderAddress(order)}</div>
          </div>
          <div className="wao-col wao-col-wide">
            <div className="wao-label">Items</div>
            {order.LineItems.map((l, i) => {
              const needs = this.lineNeeds(l);
              return (
                <div key={i} className="wao-item">
                  {this.renderProductImage(l.Product)}
                  <span>
                    {l.Qty} &times; {l.Product.Name}
                    {needs && <span className="wao-tag">{needs}</span>}
                    <span className="wao-muted"> {money(l.Amount)}</span>
                  </span>
                </div>
              );
            })}
            {(order.PackedShortfall || []).length > 0 && (
              <div className="wao-error">
                Not packed in full:{" "}
                {order.PackedShortfall.map((x) => (Number(x.packed) > 0 ? x.name + " (" + x.packed + " of " + x.ordered + ")" : x.name + " (none)")).join(", ")}
              </div>
            )}
            {Number(order.RefundDue) > 0 && (
              <div className={order.RefundStatus === "initiated" ? "wao-muted" : "wao-error"}>
                Refund {money(order.RefundDue)}: {order.RefundStatus === "initiated" ? "sent to the customer" : "to be refunded by hand from Razorpay"}
                {order.OnlineReturnNo ? " \u00b7 Online Order Return " + order.OnlineReturnNo : ""}
              </div>
            )}
          </div>
          <div className="wao-col wao-col-right">
            <div className="wao-label">Total</div>
            <div className="wao-total">{money(order.PackedBillAmount !== undefined ? order.PackedBillAmount : order.BillAmount)}</div>
            {order.PackedBillAmount !== undefined && Number(order.PackedBillAmount) !== Number(order.BillAmount) && (
              <div className="wao-muted">ordered {money(order.BillAmount)}</div>
            )}
            {this.renderActions(order)}
          </div>
        </div>
      </div>
    );
  }

  renderPackLine(order, line, index) {
    const needs = this.lineNeeds(line);
    const selection = this.state.selections[index] || {};
    const product = this.packedProduct(line, selection);
    const batchProduct = needs !== "" && this.isBatchProduct(product);
    const picked = (selection.picks || []).reduce((sum, p) => sum + Number(p.qty || 0), 0);
    const stock = this.isStockLine(line);
    const have = this.haveQty(line, selection);
    const none = stock && have === 0;

    return (
      <tr key={index} style={none ? { opacity: 0.6 } : undefined}>
        <td>
          <div className="wao-item">
            {this.renderProductImage(selection.variant || line.Product)}
            <span>
              {line.Product.Name}
              {selection.variant && <div className="wao-muted">Variant: {selection.variant.Name}</div>}
            </span>
          </div>
        </td>
        <td style={{ textAlign: "right" }}>{line.Qty}</td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {stock ? (
            <React.Fragment>
              <input
                type="number"
                min="0"
                max={line.Qty}
                step="any"
                className="wao-have"
                value={selection.qty !== undefined ? selection.qty : line.Qty}
                onChange={(e) => this.onHaveQtyChange(index, e.target.value)}
              />{" "}
              <button className="wao-btn wao-btn-light" title="None available" onClick={() => this.onHaveQtyChange(index, "0")}>
                None
              </button>
              {!(have >= 0) && <div className="wao-error">0 to {line.Qty}</div>}
            </React.Fragment>
          ) : (
            <span className="wao-muted">&mdash;</span>
          )}
        </td>
        <td style={{ textAlign: "right" }}>{money(line.RetailPrice)}</td>
        <td style={{ textAlign: "right" }}>{money(have >= 0 && stock ? (Number(line.Amount) * have) / Number(line.Qty) : line.Amount)}</td>
        <td>
          {none && <span className="wao-error">not available</span>}
          {!none && needs === "variant" && (
            <button className="wao-btn wao-btn-light" onClick={() => this.setState({ variantLine: { index: index, product: this.configProduct(line.Product) } })}>
              {selection.variant ? "Change variant" : "Select variant"}
            </button>
          )}{" "}
          {!none && batchProduct && (
            <button className="wao-btn wao-btn-light" onClick={() => this.setState({ batchLine: { index: index, product: product } })}>
              {(selection.picks || []).length > 0 ? "Change batch" : "Select batch"}
            </button>
          )}
          {!none && batchProduct &&
            (selection.picks || []).map((p, pi) => (
              <div key={pi} className="wao-pick">
                {p.batch.BatchNo || p.batch.key} &times;{" "}
                <input
                  type="number"
                  min="0"
                  max={p.batch.Qty}
                  value={p.qty}
                  onChange={(e) => this.onPickQtyChange(index, pi, e.target.value)}
                />
                <span className="wao-muted"> of {p.batch.Qty}</span>
              </div>
            ))}
          {!none && batchProduct && (selection.picks || []).length > 0 && Math.round(picked * 1000) !== Math.round(have * 1000) && (
            <div className="wao-error">
              {picked} of {have} selected
            </div>
          )}
          {!none && needs === "" && <span className="wao-muted">&mdash;</span>}
        </td>
      </tr>
    );
  }

  renderPackWindow() {
    const order = this.state.packOrder;
    if (!order) return null;
    const ready = this.packReady(order, this.state.selections);
    const totals = this.packTotals(order, this.state.selections);
    const packText = totals.refund > 0 ? "Pack and refund " + money(totals.refund) : "Convert to Sales Invoice";
    const changed = Math.round(totals.total * 100) !== Math.round(Number(order.BillAmount) * 100);
    return (
      <Window
        header={"Pack order " + order.TransactionNo}
        width={"900px"}
        processing={this.state.packing}
        onClose={() => this.setState({ packOrder: null, selections: {} })}
        buttons={[
          { Type: "secondary", Text: "Cancel", Align: "left" },
          { Type: "primary", Text: packText, Align: "right" },
        ]}
        onButtonClick={(buttonText) => {
          if (buttonText !== packText) {
            this.setState({ packOrder: null, selections: {} });
            return;
          }
          if (!ready) {
            alert(
              totals.anyPacked
                ? "Enter a quantity from 0 to the ordered quantity for every item, and select the variant / batch of what you have."
                : "None of the items is available. Close this and cancel the order instead."
            );
            return;
          }
          if (totals.refund > 0 && !window.confirm("The customer already paid " + money(totals.paid) + ". " + money(totals.refund) + " will be refunded to their payment method now. Continue?")) {
            return;
          }
          this.convertToInvoice(order, this.state.selections);
        }}
      >
        <div className="box-body" style={{ padding: "10px" }}>
          <div className="wao-muted" style={{ marginBottom: "8px" }}>
            Enter how many of each item you have (None if you don't have it), and select the variant and batch where needed. The Sales Invoice is made for these
            quantities and reduces the stock.
          </div>
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                <th>Product</th>
                <th style={{ textAlign: "right" }}>Ordered</th>
                <th style={{ textAlign: "right" }}>You have</th>
                <th style={{ textAlign: "right" }}>Price</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th>Variant / Batch</th>
              </tr>
            </thead>
            <tbody>{order.LineItems.map((l, i) => this.renderPackLine(order, l, i))}</tbody>
            <tfoot>
              <tr>
                <td colSpan="4" style={{ textAlign: "right" }}>
                  <b>New total</b>
                  {changed && <span className="wao-muted"> (ordered {money(order.BillAmount)})</span>}
                </td>
                <td style={{ textAlign: "right" }}>
                  <b>{money(totals.total)}</b>
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
          {totals.paid > 0 ? (
            totals.refund > 0 ? (
              <div className="wao-error">
                The customer already paid {money(totals.paid)} online. {money(totals.refund)} is refunded to their original payment method when you pack.
              </div>
            ) : (
              <div className="wao-muted">The customer already paid {money(totals.paid)} online.</div>
            )
          ) : (
            <div className="wao-muted">
              Cash on delivery: the customer pays <b>{money(totals.payOnDelivery)}</b> on delivery.
            </div>
          )}
        </div>
      </Window>
    );
  }

  renderDeliveredWindow() {
    const order = this.state.deliverOrder;
    if (!order) return null;
    const close = () => this.setState({ deliverOrder: null, deliveryPaymentModeID: "" });
    return (
      <Window
        header={"Delivered: " + order.TransactionNo} modal={true}
        width={"420px"}
        processing={this.state.busyKey === order.key}
        onClose={close}
        buttons={[
          { Type: "secondary", Text: "Cancel", Align: "left" },
          { Type: "primary", Text: "Delivered", Align: "right" },
        ]}
        onButtonClick={async (buttonText) => {
          if (buttonText !== "Delivered") return close();
          if (!this.state.deliveryPaymentModeID) {
            alert("Please select the payment mode");
            return;
          }
          await this.markDelivered(order, this.state.deliveryPaymentModeID);
          close();
        }}
      >
        <div className="box-body" style={{ padding: "15px" }}>
          <div style={{ marginBottom: "10px" }}>
            Cash on delivery: <b>{money(this.codDue(order))}</b>
          </div>
          <Input
            type="select"
            label="Payment mode"
            value={this.state.deliveryPaymentModeID}
            onChange={(value) => this.setState({ deliveryPaymentModeID: value })}
          >
            <option value="">Select payment mode</option>
            {this.codPaymentModes().map((p) => (
              <option key={p.key} value={p.key}>
                {p.PaymentType}
              </option>
            ))}
          </Input>
        </div>
      </Window>
    );
  }

  renderOutWindow() {
    const order = this.state.outOrder;
    if (!order) return null;
    return (
      <Window
        header={"Out for delivery: " + order.TransactionNo} modal={true}
        width={"420px"}
        processing={this.state.busyKey === order.key}
        onClose={() => this.closeOutWindow()}
        buttons={[
          { Type: "secondary", Text: "Cancel", Align: "left" },
          { Type: "primary", Text: "Out for delivery", Align: "right" },
        ]}
        onButtonClick={(buttonText) => {
          if (buttonText === "Out for delivery") this.onOutForDelivery();
          else this.closeOutWindow();
        }}
      >
        <div className="box-body" style={{ padding: "15px" }}>
          <Input
            type="select"
            label="Delivery person (optional)"
            value={this.state.deliveryPersonKey}
            onChange={(value) => this.onDeliveryPersonSelected(value)}
          >
            <option value="">Select Employee</option>
            <option value="new">Other</option>
            {this.state.employeeList.map((e) => (
              <option key={e.key} value={e.key}>
                {(e.EmployeeFirstName + " " + (e.EmployeeLastName || "")).trim()}
              </option>
            ))}
          </Input>
          <Input
            type="text"
            label="Delivery person name"
            value={this.state.deliveryPersonName}
            onChange={(value) => this.setState({ deliveryPersonName: value })}
          />
          <Input
            type="mobileno"
            label="Delivery person mobile no"
            value={this.state.deliveryPersonMobileNo}
            onChange={(value) => this.setState({ deliveryPersonMobileNo: value })}
          />
        </div>
      </Window>
    );
  }

  render() {
    const { tab, orders, counts, loading, batchLine, variantLine, packOrder, searchText } = this.state;
    const visibleOrders = searchText.trim() ? orders.filter((o) => this.orderMatches(o, searchText)) : orders;
    return (
      <div className={"header-margin" + (this.state.print ? " wao-printing" : "")}>
      <div className="wao-page">
        <div className="wao-header">
          <div>
            {/* <div className="listing-page-header-title">WhatsApp Orders</div> */}
            <div className="listing-page-header-title">Online Orders</div>
            <div className="wao-muted">New orders appear by themselves (refreshed every {REFRESH_SECONDS} seconds)</div>
          </div>
          <button className="wao-btn wao-btn-light" onClick={() => this.loadOrders(false)}>
            Refresh
          </button>
        </div>

        <div className="wao-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={"wao-tab" + (tab === t.key ? " wao-tab-active" : "")}
              onClick={() => this.setState({ tab: t.key, orders: [] }, () => this.loadOrders(false))}
            >
              {t.label}
              <span className={"wao-count" + (t.key === "pending" && counts.pending > 0 ? " wao-count-alert" : "")}>{counts[t.key] || 0}</span>
            </button>
          ))}
        </div>

        <div className="wao-search">
          <input
            type="text"
            className="form-control"
            placeholder="Search by order no, customer name, phone, email, address or item"
            value={searchText}
            onChange={(e) => this.setState({ searchText: e.target.value })}
          />
          {searchText && (
            <button className="wao-btn wao-btn-light" onClick={() => this.setState({ searchText: "" })}>
              Clear
            </button>
          )}
        </div>

        {loading && <Loader show={loading} />}
        {!loading && visibleOrders.length === 0 && (
          <div className="wao-empty">{searchText.trim() && orders.length > 0 ? "No orders match your search" : "No orders here"}</div>
        )}
        {visibleOrders.map((o) => this.renderOrder(o))}

        {this.renderPackWindow()}
        {this.renderOutWindow()}
        {this.renderDeliveredWindow()}

        {batchLine && packOrder && (
          <BatchSelection
            {...this.props}
            mobile={false}
            product={batchLine.product}
            showAllBatches={false}
            storeLocationID={packOrder.CashRegister && packOrder.CashRegister.StoreLocationID}
            lineItems={[]}
            onOK={(batches) => this.onBatchesChosen(batches)}
            onClose={() => this.setState({ batchLine: null })}
          />
        )}
        {variantLine && (
          <ProductVariants
            {...this.props}
            mobile={false}
            selectedVariantProduct={variantLine.product}
            onOK={(product) => this.onVariantChosen(product)}
            onClose={() => this.setState({ variantLine: null })}
          />
        )}
      </div>
      {/* outside the page: the page is hidden while this prints (WhatsAppOrders.css), only the invoice is on paper */}
      {this.state.print && (
        <Print
          {...this.props}
          printChecked={true}
          sendEmailChecked={false}
          sendSMSChecked={false}
          printData={this.state.printData}
          onClose={() => this.setState({ print: false, printData: null })}
        />
      )}
      </div>
    );
  }
}

export default WhatsAppOrders;
